import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  FileText, Loader2, Download, Copy, RefreshCw, TrendingDown, TrendingUp, Minus, Sparkles,
} from "lucide-react";

const TREND_CONFIG = {
  improving: { label: "En amélioration", className: "bg-green-100 text-green-700", icon: TrendingUp },
  worsening: { label: "En dégradation", className: "bg-red-100 text-red-700", icon: TrendingDown },
  stable: { label: "Stable", className: "bg-slate-100 text-slate-600", icon: Minus },
  mixed: { label: "Mixte", className: "bg-amber-100 text-amber-700", icon: Minus },
  unknown: { label: "—", className: "bg-slate-100 text-slate-500", icon: Minus },
};

function RecommendationList({ title, items, color }) {
  const colors = {
    blue: "text-blue-700",
    green: "text-green-700",
    orange: "text-orange-700",
    purple: "text-purple-700",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <h4 className={`font-semibold text-sm mb-2 ${colors[color] || colors.blue}`}>{title}</h4>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-slate-600 flex gap-2">
            <span className="text-slate-300">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function slugify(name) {
  return (name || "suivi")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

export default function BloodTestTrackExport({ trackId, trackName, reportCount }) {
  const [exportData, setExportData] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchExport = useCallback(() => {
    if (!trackId) return Promise.resolve();
    return api.get(`/blood-tests/tracks/${trackId}/export`)
      .then(r => {
        setExportData(r.data.export || null);
        setStatus(r.data.ai_export_status || null);
        setError(r.data.ai_export_error || null);
        setGeneratedAt(r.data.ai_export_at || null);
      })
      .catch(() => {
        setExportData(null);
        setStatus(null);
        setError("Impossible de charger l'export");
      });
  }, [trackId]);

  useEffect(() => {
    if (!trackId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchExport().finally(() => setLoading(false));
  }, [trackId, fetchExport]);

  const handleGenerate = async (force = false) => {
    if (!trackId) return;
    if ((reportCount || 0) < 1) {
      return toast.error("Au moins un bilan analysé est requis pour l'export");
    }
    setGenerating(true);
    setStatus("processing");
    try {
      const { data } = await api.post(
        `/blood-tests/tracks/${trackId}/export`,
        { force },
        { timeout: 120000 },
      );
      setExportData(data.export || null);
      setStatus(data.ai_export_status || null);
      setError(data.ai_export_error || null);
      setGeneratedAt(data.ai_export_at || null);
      if (data.export) {
        toast.success(force ? "Export IA régénéré" : "Export IA généré");
      } else if (data.ai_export_error) {
        toast.error(data.ai_export_error);
      }
    } catch (err) {
      const msg = err.response?.data?.ai_export_error
        || err.response?.data?.detail
        || "Erreur lors de la génération";
      setError(typeof msg === "string" ? msg : "Erreur lors de la génération");
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la génération");
      await fetchExport();
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    const text = exportData?.export_text || buildFallbackText(exportData, trackName);
    if (!text) return toast.error("Aucun texte à copier");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Export copié dans le presse-papiers");
    } catch {
      toast.error("Copie impossible sur ce navigateur");
    }
  };

  const handleDownload = () => {
    const text = exportData?.export_text || buildFallbackText(exportData, trackName);
    if (!text) return toast.error("Aucun contenu à télécharger");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export-suivi-${slugify(trackName)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export téléchargé");
  };

  if (loading) {
    return <Skeleton className="h-40 w-full rounded-2xl" />;
  }

  const exp = exportData;
  const isProcessing = status === "processing" || generating;

  return (
    <Card className="border-purple-100 bg-gradient-to-br from-purple-50/40 to-white">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Export IA du suivi
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {!exp && (
              <Button
                size="sm"
                className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => handleGenerate(false)}
                disabled={isProcessing || (reportCount || 0) < 1}
              >
                {isProcessing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Générer l'export</>
                )}
              </Button>
            )}
            {exp && (
              <>
                <Button size="sm" variant="outline" className="rounded-full" onClick={handleCopy}>
                  <Copy className="w-4 h-4 mr-2" /> Copier
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" /> Télécharger
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => handleGenerate(true)}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Regénérer
                </Button>
              </>
            )}
          </div>
        </div>
        {generatedAt && (
          <p className="text-xs text-slate-400 mt-1">
            Généré le {new Date(generatedAt.replace(" ", "T")).toLocaleString("fr-FR")}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!exp && !isProcessing && !error && (
          <p className="text-sm text-slate-500">
            Synthèse IA comparant l'évolution des biomarqueurs sur toute la durée du suivi — prête à partager avec votre coach ou à télécharger.
          </p>
        )}

        {isProcessing && !exp && (
          <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 rounded-xl px-4 py-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            Analyse comparative en cours… (30–90 s)
          </div>
        )}

        {error && !exp && (
          <div className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</div>
        )}

        {exp && (
          <>
            {exp.title && (
              <h3 className="font-heading font-bold text-slate-900">{exp.title}</h3>
            )}
            {exp.overview && (
              <p className="text-slate-700 leading-relaxed">{exp.overview}</p>
            )}
            {exp.period_summary && (
              <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-100 p-3">
                {exp.period_summary}
              </p>
            )}

            {exp.marker_trends?.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Tendances des marqueurs</h4>
                <div className="space-y-2">
                  {exp.marker_trends.slice(0, 12).map((t, i) => {
                    const cfg = TREND_CONFIG[t.trend] || TREND_CONFIG.unknown;
                    const Icon = cfg.icon;
                    return (
                      <div key={i} className="bg-white rounded-xl border border-slate-100 p-3">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{t.marker}</span>
                          <Badge className={`${cfg.className} gap-1`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </Badge>
                          {t.baseline_value && t.latest_value && (
                            <span className="text-xs text-slate-500">
                              {t.baseline_value} → {t.latest_value}{t.unit ? ` ${t.unit}` : ""}
                            </span>
                          )}
                        </div>
                        {t.change_summary && (
                          <p className="text-sm text-slate-600">{t.change_summary}</p>
                        )}
                        {t.coaching_impact && (
                          <p className="text-sm text-blue-600 mt-1">💪 {t.coaching_impact}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {exp.coaching_recommendations?.length > 0 && (
                <RecommendationList title="Recommandations coaching" items={exp.coaching_recommendations} color="blue" />
              )}
              {exp.nutrition_recommendations?.length > 0 && (
                <RecommendationList title="Recommandations nutrition" items={exp.nutrition_recommendations} color="green" />
              )}
              {exp.training_recommendations?.length > 0 && (
                <RecommendationList title="Adaptations entraînement" items={exp.training_recommendations} color="orange" />
              )}
              {exp.next_check_suggestions?.length > 0 && (
                <RecommendationList title="Prochains contrôles" items={exp.next_check_suggestions} color="purple" />
              )}
            </div>

            {exp.disclaimer && (
              <p className="text-xs text-slate-400 italic border-t border-slate-100 pt-4">{exp.disclaimer}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function buildFallbackText(exp, trackName) {
  if (!exp) return "";
  const lines = [
    `EXPORT IA — ${exp.title || trackName || "Suivi sanguin"}`,
    "",
    exp.overview || "",
    "",
    exp.period_summary || "",
    "",
  ];
  if (exp.marker_trends?.length) {
    lines.push("TENDANCES", "----------");
    exp.marker_trends.forEach(t => {
      lines.push(`• ${t.marker} (${t.trend || "?"}): ${t.change_summary || ""}`);
    });
    lines.push("");
  }
  ["coaching_recommendations", "nutrition_recommendations", "training_recommendations", "next_check_suggestions"].forEach(key => {
    if (exp[key]?.length) {
      lines.push(key.replace(/_/g, " ").toUpperCase());
      exp[key].forEach(item => lines.push(`- ${item}`));
      lines.push("");
    }
  });
  if (exp.disclaimer) lines.push(exp.disclaimer);
  return lines.join("\n").trim();
}